import { brandTheme, stencilTheme } from '@amzn/stencil-design-tokens/js/web/theme';
import { ThemeMode as StencilThemeMode } from '@amzn/stencil-react-components/context';

// Must match the SDK's THEME_MODE_OVERRIDE_KEY for cross-app testing tool compatibility.
const THEME_MODE_OVERRIDE_KEY = "__AtoZThemeOverride";
const THEME_NAME_OVERRIDE_KEY = "__AtoZThemeNameOverride";
const PREFERS_DARK_MEDIA_QUERY = "(prefers-color-scheme: dark)";

type ThemeMode = "LIGHT" | "DARK" | "DEVICE";

function getLocalStorageThemeOverride(): ThemeMode | null {
    try {
        // localStorage can throw in privacy mode, SSR, or quota-exceeded scenarios
        const value = localStorage.getItem(THEME_MODE_OVERRIDE_KEY);
        if (value === "LIGHT" || value === "DARK" || value === "DEVICE") {
            return value;
        }
        return null;
    } catch {
        return null;
    }
}

function resolveDevicePreference(): "LIGHT" | "DARK" {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
        return "LIGHT";
    }
    return window.matchMedia(PREFERS_DARK_MEDIA_QUERY).matches ? "DARK" : "LIGHT";
}

/**
 * Returns the effective theme mode for the login page.
 *
 * Resolution order:
 *   1. localStorage override (__AtoZThemeOverride) — for dev/QA testing
 *   2. OS-level prefers-color-scheme media query
 *
 * Returns uppercase "LIGHT" | "DARK" to match the original SDK return type.
 */
export function getThemeMode(): "LIGHT" | "DARK" {
    const override = getLocalStorageThemeOverride();
    if (override !== null) {
        return override === "DEVICE" ? resolveDevicePreference() : override;
    }
    return resolveDevicePreference();
}

const DEFAULT_THEME_NAME = "DEFAULT";

/**
 * Returns the current brand theme name.
 *
 * Mirrors the SDK's getThemeName() resolution order:
 *   1. localStorage override (__AtoZThemeNameOverride) — for dev/QA testing
 *   2. Fallback to 'DEFAULT' (no AtoZContext on the login page)
 */
export function getThemeName(): string {
    if (typeof window === "undefined") return DEFAULT_THEME_NAME;
    try {
        const stored = localStorage.getItem(THEME_NAME_OVERRIDE_KEY);
        if (stored !== null) {
            return stored;
        }
    } catch {
        // localStorage unavailable
    }
    return DEFAULT_THEME_NAME;
}

/**
 * Returns the resolved Stencil theme mode enum value.
 */
export function getStencilThemeMode(): StencilThemeMode {
    return getThemeMode() === 'DARK'
        ? StencilThemeMode.Dark
        : StencilThemeMode.Light;
}

/**
 * Returns the resolved Stencil theme object based on the current theme name.
 */
export function getStencilTheme(): typeof stencilTheme {
    const themeName = getThemeName();
    const themeMap: Record<string, typeof stencilTheme> = {
        'DEFAULT': stencilTheme,
        'BRAND': brandTheme,
    };
    return themeMap[themeName] ?? themeMap['DEFAULT'];
}
